import React, { useEffect, useState } from "react";
import { Wrench, X } from "lucide-react";

/**
 * iter121 — UK overnight window helper. Returns true when London local time
 * is between 23:00 (inclusive) and 07:00 (exclusive). Used as a fallback
 * trigger for the MaintenanceBanner when the /api/maintenance/status
 * endpoint times out during heavy background work.
 */
function _inOvernightWindowUk() {
  try {
    // toLocaleString in Europe/London handles BST/GMT automatically
    const ukNow = new Date(new Date().toLocaleString("en-GB", { timeZone: "Europe/London" }));
    const hour = ukNow.getHours();
    return hour >= 23 || hour < 7;
  } catch {
    return false;
  }
}

/**
 * Maintenance banner — non-blocking.
 *
 * iter117 (May 31 2026): switched from a full-screen splash to a soft top
 * banner per Anthony's request. App is fully usable during the overnight
 * AI-enrichment window (23:00 → 07:00 UK); the banner just lets visitors
 * know we're working on improvements and apologises in advance if anything
 * feels slow because the worker is grinding through Claude calls.
 *
 * Polls /api/maintenance/status every 30 seconds (and on first mount).
 * Dismissible — once a user closes it, we don't re-show it in the same
 * tab session (localStorage keyed by `started_at` so a NEW maintenance
 * window re-displays the banner).
 *
 * Admin routes (/admin*, /login*) never see the banner so the admin can
 * always toggle maintenance off without distraction.
 */
export default function MaintenanceGate({ children }) {
  const [status, setStatus] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(null);

  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const isAdminRoute = path.startsWith("/admin") || path.startsWith("/login");

  useEffect(() => {
    let cancel = false;
    let timer = null;
    const tick = async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        // iter121 — 6s abort so a slow backend during overnight enrichment
        // can't keep us in a never-ending fetch state.
        const ctrl = new AbortController();
        const tm = setTimeout(() => ctrl.abort(), 6000);
        const r = await fetch(`${apiBase}/api/maintenance/status`, {
          headers: { Accept: "application/json" },
          signal: ctrl.signal,
        });
        clearTimeout(tm);
        if (!r.ok) throw new Error("status err");
        const json = await r.json();
        if (!cancel) setStatus(json);
      } catch {
        // iter121 — schedule fallback. When the backend is too slow to
        // answer (e.g. during heavy AI-enrichment) we'd previously hide
        // the banner. Now we fall back to a time-of-day check so the
        // overnight 23:00–07:00 UK window still surfaces the banner with
        // a generic message. Falls back to OFF outside that window.
        if (cancel) return;
        const inWindow = _inOvernightWindowUk();
        if (inWindow) {
          setStatus({
            enabled: true,
            message:
              "We're making improvements overnight — apologies if anything feels slow. The app is fully usable. — Anthony 🏕",
            // Use the date so dismiss key is stable across the night
            started_at: new Date().toISOString().slice(0, 10) + "T23:00:00Z",
            estimated_done_at: "07:00 UK time",
          });
        } else {
          setStatus({ enabled: false });
        }
      }
    };
    tick();
    timer = setInterval(tick, 30000);
    return () => {
      cancel = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  // Read dismissed marker for the current maintenance window
  useEffect(() => {
    if (!status?.started_at) return;
    try {
      const k = sessionStorage.getItem("cc_maintenance_dismissed_until");
      if (k === status.started_at) setDismissedKey(k);
    } catch { /* sessionStorage unavailable — ignore */ }
  }, [status?.started_at]);

  const dismiss = () => {
    if (!status?.started_at) return;
    try { sessionStorage.setItem("cc_maintenance_dismissed_until", status.started_at); } catch { /* ignore */ }
    setDismissedKey(status.started_at);
  };

  const showBanner =
    status?.enabled &&
    !isAdminRoute &&
    dismissedKey !== status?.started_at;

  return (
    <>
      {showBanner && <MaintenanceBanner status={status} onDismiss={dismiss} />}
      {children}
    </>
  );
}

function MaintenanceBanner({ status, onDismiss }) {
  const message = status?.message || DEFAULT_MESSAGE;
  return (
    <div
      className="w-full bg-[#fff4ee] border-b-2 border-[#ff4500] text-[#111]"
      data-testid="maintenance-splash"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Wrench className="w-4 h-4 text-[#ff4500]" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] sm:text-sm leading-snug">
            <strong className="font-bold text-[#ff4500] mr-1">Improving CampCompass overnight</strong>
            <span className="text-[#222]">{message}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 p-1 -m-1 text-[#888] hover:text-[#ff4500]"
          aria-label="Dismiss maintenance notice"
          data-testid="maintenance-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const DEFAULT_MESSAGE =
  "We're making improvements right now — imagine local walks plotted where your campsite is, plus a fresh, AI-written description for every site in the database. Coming soon. If the app feels a touch slower while we work on it, sorry — this one's for you. — Anthony 🏕";
